/* eslint-disable */
/**
 * @file This file defines the Sidebar component, which serves as the main container
 * for all filter controls. It dynamically renders filters based on the current page type,
 * manages filter state interactions (setting, applying, clearing), handles URL parameter
 * synchronization, performs basic validation, and includes logic for mobile accordion behavior.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFilters, FilterActionTypes } from '../../context/FilterContext/FilterContext';
import ZipCodeFilter from '../ZipCodeFilter';
import AgeFilter from '../AgeFilter/AgeFilter';
import MainTypeFilter from '../MainTypeFilter';
import Subtype from '../Subtype';
import DrugInterventionFilter from '../DrugInterventionFilter/DrugInterventionFilter';
import StageFilter from '../StageFilter';
import { PAGE_FILTER_CONFIGS } from '../../config/pageFilterConfigs';
import './Sidebar.scss';
// import { useStateValue } from '../../../../store/store'; // Unused import
import { FILTER_EVENTS, INTERACTION_TYPES } from '../../tracking/filterEvents';
import { useFilterCounters } from '../../hooks/useFilterCounters';
// import { formatLocationString, getAppliedFieldsString } from '../../utils/analytics'; // Unused imports
import { useTracking } from 'react-tracking';
import { URL_PARAM_MAPPING } from '../../constants/urlParams';
import { isValidZipFormat } from '../../utils/locationUtils';
import PropTypes from 'prop-types';
import AppliedFilters from '../AppliedFilters/AppliedFilters';
import { getFieldCode } from '../../utils/eddlAnalytics';
import { BREAKPOINTS } from '../../../../constants/breakpoints';

/**
 * Helper function to detect which field was added by comparing old and new filters
 * @param {object} prevFilters - Previous filter state
 * @param {object} newFilters - New filter state
 * @returns {string|null} Field code that was added (e.g., 'a', 't', 'loc') or null
 */
const detectAddedField = (prevFilters, newFilters) => {
	// Check each filter type to see if it was added or changed
	const filterTypes = ['maintype', 'subtype', 'stage', 'drugIntervention', 'age', 'location'];

	for (const filterType of filterTypes) {
		const prevValue = prevFilters[filterType];
		const newValue = newFilters[filterType];

		// Check if this field was added or changed
		if (filterType === 'location') {
			// For location, check if zipCode was added
			const prevZip = prevValue?.zipCode;
			const newZip = newValue?.zipCode;
			if (!prevZip && newZip) {
				return getFieldCode(filterType);
			}
		} else if (filterType === 'age') {
			// For age, check if value was added
			if (!prevValue && newValue) {
				return getFieldCode(filterType);
			}
		} else {
			// For array filters (maintype, subtype, stage, drugIntervention)
			const prevLength = Array.isArray(prevValue) ? prevValue.length : 0;
			const newLength = Array.isArray(newValue) ? newValue.length : 0;
			if (prevLength === 0 && newLength > 0) {
				// Field was added
				return getFieldCode(filterType);
			} else if (newLength > prevLength) {
				// More items added
				return getFieldCode(filterType);
			}
		}
	}

	return null; // No field added, might be a modification
};

/**
 * Renders the filter sidebar, including relevant filter components based on pageType.
 * Handles applying, clearing, and validating filters, as well as URL updates and tracking.
 *
 * @param {object} props - The component props.
 * @param {string} [props.pageType='Disease'] - The type of page, determining which filters are shown (e.g., 'Disease', 'Intervention').
 * @param {boolean} [props.isDisabled=false] - If true, disables all filter interactions.
 * @param {number|null} [props.initialTotalCount=null] - Initial total count of trials (currently unused).
 * @param {Function} [props.onFilterApplied=()=>{}] - Callback function invoked when filters are successfully applied. Receives current filters and apply count.
 * @param {Function} [props.onFilterCleared=()=>{}] - Callback function invoked when filters are cleared. Receives clear count and apply count.
 * @returns {JSX.Element|null} The rendered Sidebar component or null if pageType is invalid.
 */
const Sidebar = ({ pageType = 'Disease', isDisabled = false, onFilterApplied = () => {}, onFilterCleared = () => {} }) => {
	// Hooks for navigation, location, filter context, tracking, and counters
	const navigate = useNavigate();
	const location = useLocation();
	const { state, dispatch, applyFilters, enabledFilters = [], listingInfo } = useFilters();
	const { filters, isDirty, isInvalidQuery } = state; // Get current filters, dirty state, and invalid query state from context
	const [hasInteracted, setHasInteracted] = useState(false); // Tracks if user has interacted with any filter yet
	// const [isFirstLoad, setIsFirstLoad] = useState(true); // Unused state variable
	// State to hold the function that retrieves the latest ZIP validation status from ZipCodeFilter
	const [getZipValidationStatus, setGetZipValidationStatus] = useState(null);
	const tracking = useTracking(); // React-tracking hook
	const { incrementRemovedCounter } = useFilterCounters();
	const prevAppliedFiltersRef = useRef();
	const isInitialUrlLoadRef = useRef(true); // Track if this is the first load from URL params
	const pendingRemovedFieldRef = useRef(null); // Track which field was just removed
	const pendingAddedFieldRef = useRef(null); // Track which field was just added

	/**
	 * Validates the current filter values (age range, zip format, radius presence).
	 * @returns {object} An object containing error messages keyed by field name, or an empty object if valid.
	 */
	const validateFilters = () => {
		const errors = {};

		// Validate location filter (ZIP code format and radius presence)
		if (filters.location?.zipCode) {
			if (!isValidZipFormat(filters.location.zipCode)) {
				errors.location = 'Invalid ZIP code format. Please enter a 5-digit US ZIP code.';
			}
			// Radius is required only if a ZIP code is entered
			if (!filters.location.radius) {
				errors.radius = 'Radius is required when ZIP code is provided.';
			}
		}

		return errors;
	};

	/**
	 * Maps internal error field names to corresponding URL parameter short codes for tracking.
	 * @param {object} errors - The error object returned by validateFilters.
	 * @returns {string} A comma-separated string of URL parameter short codes corresponding to the errors.
	 */
	const getErrorFields = (errors) => {
		return Object.keys(errors)
			.map((field) => {
				switch (field) {
					case 'age':
						return URL_PARAM_MAPPING.age.shortCode;
					case 'location': // Map both location and radius errors to the zip code parameter
					case 'radius':
						return URL_PARAM_MAPPING.zipCode.shortCode;
					default:
						return field; // Fallback for unexpected fields
				}
			})
			.join(',');
	};

	/**
	 * Tracks the first interaction with any filter control.
	 * Sets the `hasInteracted` state to prevent repeated tracking.
	 * @param {string} fieldName - The name of the filter field being interacted with.
	 */
	const trackFilterStart = (fieldName) => {
		if (!hasInteracted) {
			tracking.trackEvent({
				type: 'Other',
				event: FILTER_EVENTS.START,
				linkName: FILTER_EVENTS.START, // Consider renaming linkName for clarity
				interactionType: INTERACTION_TYPES.FILTER_START,
				fieldInteractedWith: fieldName,
			});
			setHasInteracted(true); // Mark interaction to prevent re-tracking
		}
	};

	/**
	 * Handles changes to the Zip Code input.
	 * Dispatches a SET_FILTER action, automatically setting a default radius if zip is entered.
	 * @param {string} value - The new zip code value.
	 */
	const handleZipCodeChange = (value) => {
		dispatch({
			type: FilterActionTypes.SET_FILTER,
			payload: {
				filterType: 'location',
				value: {
					...filters.location,
					zipCode: value,
					// Set default radius if zip is entered and no radius exists, clear radius if zip is cleared
					radius: value ? filters.location?.radius || '100' : null,
				},
			},
		});
	};

	/**
	 * Handles changes to the Radius select input.
	 * Dispatches a SET_FILTER action to update the radius.
	 * @param {React.ChangeEvent<HTMLSelectElement>} e - The select change event.
	 */
	const handleRadiusChange = (e) => {
		dispatch({
			type: FilterActionTypes.SET_FILTER,
			payload: {
				filterType: 'location',
				value: {
					...filters.location,
					radius: e.target.value,
				},
			},
		});
	};

	/**
	 * Handles the "Clear Filters" button click.
	 * Increments the removed counter, calls the parent callback,
	 * dispatches actions to clear and re-apply (empty) filters.
	 */
	const handleClearFilters = () => {
		// Increment the removed counter
		incrementRemovedCounter();

		// Mark that we're clearing all filters
		pendingRemovedFieldRef.current = 'all';

		// Dispatch actions to update context state
		dispatch({ type: FilterActionTypes.SET_INVALID_QUERY, payload: false }); // Clear invalid query error
		dispatch({ type: FilterActionTypes.CLEAR_FILTERS });
		dispatch({ type: FilterActionTypes.APPLY_FILTERS }); // Apply the cleared state

		// The auto-apply detection effect will handle calling onFilterCleared when filters update

		// TODO: Clear URL parameters as well? Currently only ApplyFilters updates URL.
		// navigate(window.location.pathname); // Option 1: Navigate to path without params
	};

	/**
	 * Handles removal of an individual filter from the AppliedFilters component.
	 * Increments the removed counter and marks which field was removed.
	 * The auto-apply detection effect will handle the callback with proper filters.
	 * @param {string} fieldCode - The field code that was removed (e.g., 'a', 'loc', 't')
	 */
	const handleIndividualFilterRemoved = (fieldCode) => {
		// Increment the removed counter
		incrementRemovedCounter();

		// Mark which field was removed so the auto-apply effect knows to call onFilterCleared
		pendingRemovedFieldRef.current = fieldCode;

		// The REMOVE_FILTER action has already been dispatched by AppliedFilters
		// The auto-apply detection effect will handle calling onFilterCleared when filters update
	};

	/**
	 * Handles the "Apply Filters" button click.
	 * Performs validation, handles ZIP code validation result, tracks events,
	 * calls the applyFilters action from context, and updates URL parameters.
	 */
	const handleApplyFilters = async () => {
		// Do nothing if filters haven't changed
		if (!isDirty) return;

		// console.log('Sidebar - handleApplyFilters - Current filters:', filters);
		// console.log('Sidebar - handleApplyFilters - isDirty:', isDirty);

		// Validate form field values (e.g., age range)
		const errors = validateFilters();
		const hasErrors = Object.keys(errors).length > 0;

		// If validation errors exist, track and stop
		if (hasErrors) {
			tracking.trackEvent({
				type: 'Other',
				event: FILTER_EVENTS.APPLY_ERROR,
				linkName: FILTER_EVENTS.APPLY_ERROR,
				interactionType: INTERACTION_TYPES.APPLIED_WITH_ERRORS,
				errorField: getErrorFields(errors),
			});
			// TODO: Display validation errors to the user
			return;
		}

		// Handle asynchronous ZIP code validation
		if (filters.location?.zipCode) {
			// Ensure the validation status function is available
			if (!getZipValidationStatus) {
				// This case should ideally not happen if ZipCodeFilter is working correctly
				// Consider adding user feedback or logging here
				return;
			}

			// Retrieve the latest validation status from the ZipCodeFilter component
			const validationResult = getZipValidationStatus();

			// If the stored validation status indicates the ZIP is invalid, track and stop
			if (!validationResult.isValid) {
				tracking.trackEvent({
					type: 'Other',
					event: FILTER_EVENTS.APPLY_ERROR,
					linkName: FILTER_EVENTS.APPLY_ERROR,
					interactionType: INTERACTION_TYPES.APPLIED_WITH_ERRORS,
					errorField: URL_PARAM_MAPPING.zipCode.shortCode, // Track 'zip' error
				});
				// TODO: Ensure ZipCodeFilter displays its error state
				return;
			}

			// If valid and coordinates are available, ensure they are set in the context state
			// This might be redundant if ZipCodeFilter already dispatched on validation success
			if (validationResult.isValid && validationResult.coordinates) {
				dispatch({
					type: FilterActionTypes.SET_ZIP_COORDINATES,
					payload: validationResult.coordinates,
				});
			}
		}

		// --- If all validations pass ---

		// Call the applyFilters function from context (likely triggers API call)
		await applyFilters();

		// --- Update URL parameters after successful application ---
		const params = new URLSearchParams(window.location.search);

		// Update 'a' parameter for age
		if (filters.age && filters.age.toString().trim() !== '') {
			params.set(URL_PARAM_MAPPING.age.shortCode, filters.age);
		} else {
			params.delete(URL_PARAM_MAPPING.age.shortCode);
		}

		if (filters.maintype && filters.maintype.length > 0) {
			params.set(URL_PARAM_MAPPING.maintype.shortCode, filters.maintype.join(','));
		} else {
			params.delete(URL_PARAM_MAPPING.maintype.shortCode);
		}

		// Update subtype parameter
		if (filters.subtype && filters.subtype.length > 0) {
			params.set(URL_PARAM_MAPPING.subtype.shortCode, filters.subtype.join(','));
		} else {
			params.delete(URL_PARAM_MAPPING.subtype.shortCode);
		}

		// Update 'z' parameter for zip code
		if (filters.location?.zipCode && isValidZipFormat(filters.location.zipCode)) {
			params.set(URL_PARAM_MAPPING.zipCode.shortCode, filters.location.zipCode);
		} else {
			params.delete(URL_PARAM_MAPPING.zipCode.shortCode);
		}

		// Update 'rl' parameter for radius (only if zip exists)
		if (filters.location?.zipCode && filters.location.radius) {
			params.set(URL_PARAM_MAPPING.radius.shortCode, filters.location.radius);
		} else {
			params.delete(URL_PARAM_MAPPING.radius.shortCode);
		}

		// Always reset to page 1 ('pn') when filters are applied
		params.set(URL_PARAM_MAPPING.page.shortCode, '1');

		// Update the URL using navigate (replace avoids adding to history stack)
		navigate(`${window.location.pathname}?${params.toString()}`, { replace: true });

		// Call the onFilterApplied callback for analytics
		onFilterApplied(state.appliedFilters, filterAppliedCounter);
	};

	/**
	 * Callback passed to ZipCodeFilter to receive either the validation function
	 * or the direct validation result (e.g., on initial load from URL).
	 * Stores the validation function or result in state.
	 */
	const handleZipValidationChange = useCallback(
		(validationOrResult) => {
			// Case 1: Received the validation function from ZipCodeFilter
			if (typeof validationOrResult === 'function') {
				setGetZipValidationStatus(() => validationOrResult);
				return;
			}

			// Case 2: Received a direct validation result object (e.g., from initial URL check)
			if (validationOrResult && typeof validationOrResult === 'object') {
				// Store a function that returns this static result
				setGetZipValidationStatus(() => () => validationOrResult);

				// If the initial result is valid, immediately set coordinates in context
				if (validationOrResult.isValid && validationOrResult.coordinates) {
					dispatch({
						type: FilterActionTypes.SET_ZIP_COORDINATES,
						payload: validationOrResult.coordinates,
					});
				}
			}
		},
		[dispatch]
	);

	/**
	 * Renders the appropriate filter component based on the filter type string.
	 * @param {string} filterType - The type of filter to render (e.g., 'age', 'location').
	 * @param {boolean} isDisabled - Whether the filter should be disabled.
	 * @returns {JSX.Element|null} The rendered filter component or null.
	 */
	const renderFilter = (filterType, isDisabled) => {
		switch (filterType) {
			case 'drugIntervention':
				return <DrugInterventionFilter onFocus={() => trackFilterStart(filterType)} disabled={isDisabled} />;
			case 'maintype':
				return <MainTypeFilter onFocus={() => trackFilterStart(filterType)} disabled={isDisabled} dispatch={dispatch} />;
			case 'subtype':
				return <Subtype onFocus={() => trackFilterStart(filterType)} disabled={isDisabled} dispatch={dispatch} />;
			case 'stage':
				return <StageFilter onFocus={() => trackFilterStart(filterType)} disabled={isDisabled} dispatch={dispatch} />;
			case 'age':
				return (
					<AgeFilter
						value={filters.age} // AgeFilter reads value from context
						onFocus={() => trackFilterStart(filterType)}
						disabled={isDisabled}
					/>
				);
			case 'location':
				return (
					<ZipCodeFilter
						zipCode={filters.location?.zipCode || ''} // Pass values from context
						radius={filters.location?.radius || ''}
						onZipCodeChange={handleZipCodeChange} // Handle state updates via callbacks
						onRadiusChange={handleRadiusChange}
						onValidationChange={handleZipValidationChange} // Pass callback to get validation status
						onFocus={() => trackFilterStart(filterType)}
						disabled={isDisabled}
					/>
				);
			// Add cases for other filter types (e.g., trial type, phase) if needed
			default:
				// Log error for unhandled filter types during development
				// console.warn(`Sidebar: Unhandled filter type "${filterType}"`);
				return null;
		}
	};

	// SVG data URLs for accordion button background images
	const minusSign = `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="14" height="2" viewBox="0 0 14 2" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M14 2H0V0H14V2Z" fill="%231B1B1B"/></svg>')`;
	const plusSign = `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M14 8H8V14H6V8H0V6H6V0H8V6H14V8Z" fill="%231B1B1B"/></svg>')`;

	/**
	 * Handles the click event for the mobile accordion toggle button.
	 * Toggles visibility and updates button style.
	 * Note: This directly manipulates DOM and might conflict with context-based sidebar state.
	 */
	const accordionOnClick = () => {
		const filterBtn = document.getElementById('filterButton');
		const content = document.getElementById('accordionContent');
		if (!filterBtn || !content) return; // Guard against elements not found

		filterBtn.classList.toggle('is-closed');
		const isClosed = filterBtn.classList.contains('is-closed');

		filterBtn.style.backgroundImage = isClosed ? plusSign : minusSign;
		content.hidden = isClosed;
		filterBtn.setAttribute('aria-expanded', !isClosed); // Update ARIA state
	};

	/**
	 * Sets up the event listener for the mobile accordion based on media query.
	 * Attaches/detaches the click listener and manages initial state.
	 * Note: This uses direct DOM manipulation and might be better handled via context/state.
	 */
	const setMobileOnClick = () => {
		const filterBtn = document.getElementById('filterButton');
		const content = document.getElementById('accordionContent');
		if (!filterBtn || !content) return; // Guard if elements don't exist yet

		const mobileSize = '(max-width: 1023px)'; // Define mobile breakpoint
		const mediaQueryMobile = window.matchMedia(mobileSize);

		// Function to handle media query changes
		function handleMediaQueryChange(event) {
			if (event.matches) {
				// If mobile view
				filterBtn.addEventListener('click', accordionOnClick);
				// Ensure initial state matches button class/style if needed
				const isClosed = filterBtn.classList.contains('is-closed');
				content.hidden = isClosed;
				filterBtn.setAttribute('aria-expanded', !isClosed);
				filterBtn.setAttribute('tabIndex', '0');
			} else {
				// If desktop view
				// Ensure accordion is open and listener is removed
				filterBtn.removeEventListener('click', accordionOnClick);
				filterBtn.classList.remove('is-closed');
				filterBtn.style.backgroundImage = minusSign; // Default to open style
				content.removeAttribute('hidden');
				filterBtn.setAttribute('aria-expanded', 'true');
				filterBtn.setAttribute('tabIndex', '-1');
			}
		}

		// Initial check and setup listener
		handleMediaQueryChange(mediaQueryMobile);
		// Use addEventListener for modern browsers, consider compatibility if needed
		mediaQueryMobile.addEventListener('change', handleMediaQueryChange);

		// Cleanup function for useEffect
		return () => {
			mediaQueryMobile.removeEventListener('change', handleMediaQueryChange);
			filterBtn.removeEventListener('click', accordionOnClick); // Ensure listener is removed on unmount
		};
	};

	/**
	 * Effect to set up the mobile accordion listener on component mount.
	 */
	useEffect(() => {
		const cleanup = setMobileOnClick();
		return cleanup; // Return cleanup function
	}, []); // No dependencies needed since function doesn't depend on props/state

	/**
	 * Effect to handle invalid query state:
	 * 1. Blur any focused input to prevent focus remaining on cleared fields
	 * 2. Scroll to the filter/error area on mobile so the error message is visible
	 */
	useEffect(() => {
		if (isInvalidQuery) {
			// Blur any focused element to prevent focus remaining on cleared fields
			if (document.activeElement && document.activeElement !== document.body) {
				document.activeElement.blur();
			}

			// On mobile/tablet, scroll to show the error message
			if (window.innerWidth < BREAKPOINTS.DESKTOP) {
				// Small delay to ensure the error message is rendered
				const scrollTimer = setTimeout(() => {
					const sidebar = document.querySelector('.ctla-sidebar');
					if (sidebar) {
						const sidebarRect = sidebar.getBoundingClientRect();
						const scrollTop = window.pageYOffset + sidebarRect.top - 20; // 20px offset from top
						window.scrollTo({ top: scrollTop, behavior: 'smooth' });
					}
				}, 100);
				return () => clearTimeout(scrollTimer);
			}
		}
	}, [isInvalidQuery]);

	/**
	 * Checks if any filters (age or location) are currently active.
	 * Used to enable/disable the "Clear Filters" button.
	 * @returns {boolean} True if at least one filter is active, false otherwise.
	 */
	const hasActiveFilters = () => {
		// Check age filter (handles single value or potentially array in future)
		const hasAgeFilter = filters.age != null && filters.age !== '' && filters.age.length !== 0;

		// Check location filter (zip code must exist)
		const hasLocationFilter = Boolean(filters.location?.zipCode);

		// Add checks for other filter types here if they are added
		// const hasTrialTypeFilter = filters.trialType?.length > 0;
		const hasMainTypeFilter = Array.isArray(filters.maintype) && filters.maintype.length > 0;
		const hasSubTypeFilter = Array.isArray(filters.subtype) && filters.subtype.length > 0;
		const hasDrugInterventionFilter = Array.isArray(filters.drugIntervention) && filters.drugIntervention.length > 0;
		const hasStageFilter = Array.isArray(filters.stage) && filters.stage.length > 0;

		return hasAgeFilter || hasLocationFilter || hasMainTypeFilter || hasSubTypeFilter || hasDrugInterventionFilter || hasStageFilter;
	};

	/**
	 * Effect to initialize filters from URL parameters on initial load.
	 * Only runs once on component mount.
	 */
	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const age = params.get(URL_PARAM_MAPPING.age.shortCode);
		const zip = params.get(URL_PARAM_MAPPING.zipCode.shortCode);
		const radius = params.get(URL_PARAM_MAPPING.radius.shortCode);
		const maintype = params.get(URL_PARAM_MAPPING.maintype.shortCode);
		const subtype = params.get(URL_PARAM_MAPPING.subtype.shortCode);
		const stage = params.get(URL_PARAM_MAPPING.stage.shortCode);

		let needsApply = false; // Flag to check if APPLY_FILTERS needs dispatch

		// Initialize Age filter
		if (age) {
			dispatch({
				type: FilterActionTypes.SET_FILTER,
				payload: { filterType: 'age', value: age }, // Store as number
			});
			needsApply = true;
		}

		// Initialize Location filter
		if (zip && isValidZipFormat(zip)) {
			dispatch({
				type: FilterActionTypes.SET_FILTER,
				payload: {
					filterType: 'location',
					value: { zipCode: zip, radius: radius || '100' }, // Use radius from URL or default
				},
			});
			needsApply = true;
			// Note: Zip validation and coordinate setting will be handled by ZipCodeFilter's
			// onValidationChange callback triggered by its own useEffect.
		}

		if (maintype) {
			dispatch({
				type: FilterActionTypes.SET_FILTER,
				payload: {
					filterType: 'maintype',
					value: maintype.split(','),
				},
			});
			needsApply = true;
		}

		if (subtype) {
			dispatch({
				type: FilterActionTypes.SET_FILTER,
				payload: {
					filterType: 'subtype',
					value: subtype.split(','),
				},
			});
			needsApply = true;
		}

		if (stage) {
			dispatch({
				type: FilterActionTypes.SET_FILTER,
				payload: {
					filterType: 'stage',
					value: stage.split(','),
				},
			});
			needsApply = true;
		}

		// Note: Not handling drugIntervention here - FilterContext handles it
		// to avoid race conditions with drug data loading

		// If any filters were set from URL, dispatch APPLY_FILTERS to mark state as non-dirty
		if (needsApply) {
			dispatch({ type: FilterActionTypes.APPLY_FILTERS });
		}

		// Note: isInitialUrlLoadRef will be flipped to false by the auto-apply detection effect
		// after it skips the first URL-based apply. This ensures correct timing.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Empty dependency array ensures this runs only once on mount

	// TODO: Remove this effect - auto-apply analytics should be handled by the view
	// when results are available, not immediately when filters change
	//
	// Effect to detect auto-apply and trigger analytics callback
	useEffect(() => {
		const { appliedFilters, isInitialLoad } = state;

		// Capture previous value at the start to avoid race conditions
		const prevFilters = prevAppliedFiltersRef.current;

		// Skip on first render (when prevAppliedFiltersRef.current is undefined)
		if (prevFilters === undefined) {
			prevAppliedFiltersRef.current = appliedFilters;
			return;
		}

		// Skip if this is the initial URL load - don't fire analytics for URL params
		// After skipping once, mark initial load as complete
		if (isInitialUrlLoadRef.current) {
			prevAppliedFiltersRef.current = appliedFilters;
			isInitialUrlLoadRef.current = false; // Mark initial load as complete
			return;
		}

		// Skip if this change is from URL navigation (e.g., back button)
		// isInitialLoad is true when FilterContext applies filters from URL params
		if (isInitialLoad) {
			prevAppliedFiltersRef.current = appliedFilters;
			return;
		}

		// Check if appliedFilters changed but it wasn't due to manual apply
		// This indicates auto-apply happened
		if (appliedFilters !== prevFilters && !isDirty) {
			// Check if this was a filter removal
			if (pendingRemovedFieldRef.current) {
				// Filter removal detected - call the onFilterCleared callback
				onFilterCleared(appliedFilters, pendingRemovedFieldRef.current);
				// Clear the pending field
				pendingRemovedFieldRef.current = null;
			} else {
				// Filter addition/modification detected
				// Determine which field was added by comparing old vs new filters
				const fieldAdded = detectAddedField(prevFilters || {}, appliedFilters);

				// Call the onFilterApplied callback with field information
				// Note: resultCount will be updated when the view processes the results
				onFilterApplied(appliedFilters, fieldAdded);
			}
		}

		// Update ref AFTER all comparisons are done
		prevAppliedFiltersRef.current = appliedFilters;
	}, [state.appliedFilters, isDirty, onFilterApplied, onFilterCleared]);

	// Validate pageType and configuration existence
	if (!pageType || !PAGE_FILTER_CONFIGS[pageType]) {
		// Log error during development, return null to prevent rendering
		// console.error('Sidebar: Invalid or missing pageType configuration:', pageType);
		return null;
	}

	const renderClearButton = () => {
		if (hasActiveFilters()) {
			return (
				<div className="ctla-sidebar__actions">
					<button className="usa-button ctla-sidebar__button--clear ctla-sidebar__button--full-width" onClick={handleClearFilters} disabled={isDisabled}>
						Clear Filters
					</button>
				</div>
			);
		}
	};

	const renderAppliedFilters = () => {
		if (hasActiveFilters()) {
			return (
				<AppliedFilters pageType={pageType} onFilterRemoved={handleIndividualFilterRemoved} />
			);
		}
		return null;
	};

	// Near the top of the component
	// console.log('Sidebar - Rendering with isDirty:', state.isDirty);
	// console.log('Sidebar - Current filters:', filters);

	const renderInvalidQueryMessage = () => {
		if (isInvalidQuery) {
			return (
				<div className="usa-alert usa-alert--error usa-alert--slim" role="alert">
					<div className="usa-alert__body">
						<p className="usa-alert__text">
							Sorry, there seems to be invalid criteria. Please update your filters.
						</p>
					</div>
				</div>
			);
		}
		return null;
	};

	return (
		<aside className="ctla-sidebar">
			{/* Accordion Header for Mobile */}
			<div className="usa-accordion ctla-sidebar__header">
				<h2 className="usa-accordion__heading ctla-sidebar__title">
					<button id="filterButton" type="button" className="usa-accordion__button" aria-expanded="true" aria-controls="accordionContent">
						Filter Trials
					</button>
				</h2>
			</div>
			<div id="accordionContent" className="usa-accordion__content ctla-sidebar__content">
				{renderInvalidQueryMessage()}
				{PAGE_FILTER_CONFIGS[pageType].order.map((filterType) => {
					if (enabledFilters.includes(filterType)) {
						return <div key={filterType}>{renderFilter(filterType, isDisabled)}</div>;
					}
					return null;
				})}
				{renderAppliedFilters()}
				{renderClearButton()}
			</div>
		</aside>
	);
};

// Define PropTypes for type checking and documentation
Sidebar.propTypes = {
	/** The type of page, determining which filters are shown (e.g., 'Disease', 'Intervention'). Defaults to 'Disease'. */
	pageType: PropTypes.string,
	/** If true, disables all filter interactions. Defaults to false. */
	isDisabled: PropTypes.bool,
	/** Callback function invoked when filters are successfully applied. Receives current filters and apply count. */
	onFilterApplied: PropTypes.func,
	/** Callback function invoked when filters are cleared. Receives clear count and apply count. */
	onFilterCleared: PropTypes.func,
};

export default Sidebar;
